(() => {
  const CHAIR_HANDLE = 'clean-coastal-inflatable-beach-chairs';
  const normalize = (value) => String(value || '').trim().toLowerCase();
  const routesRoot = () => window.Shopify?.routes?.root || '/';
  const route = (path) => routesRoot() + path.replace(/^\/+/, '');

  const formatMoney = (cents, currency = 'USD') =>
    new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency
    }).format(Number(cents || 0) / 100);

  class CCCartPairUpsell extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ccInitialized === 'true') return;
      this.dataset.ccInitialized = 'true';

      try {
        const data = JSON.parse(this.querySelector('[data-cc-pair-variants]').textContent);
        this.variants = Array.isArray(data.variants) ? data.variants : [];
      } catch (error) {
        this.variants = [];
      }

      this.threshold = Number(this.dataset.freeShippingThreshold || 10000);
      this.shippingCost = Number(this.dataset.standardShippingCost || 998);
      this.gate = this.querySelector('[data-cc-pair-gate]');
      this.gateAdd = this.querySelector('[data-cc-pair-gate-add]');
      this.gateContinue = this.querySelector('[data-cc-pair-gate-continue]');
      this.gateClose = this.querySelector('[data-cc-pair-gate-close]');
      this.gateStatus = this.querySelector('[data-cc-pair-gate-status]');
      this.button = this.querySelector('[data-cc-pair-add]');
      this.status = this.querySelector('[data-cc-pair-status]');
      this.swatches = [...this.querySelectorAll('[data-cc-pair-swatch]')];

      this.button?.addEventListener('click', () => this.addChair(false));
      this.gateAdd?.addEventListener('click', () => this.addChair(true));
      this.gateContinue?.addEventListener('click', () => {
        this.gate?.close();
        this.resumeCheckout();
      });
      this.gateClose?.addEventListener('click', () => this.gate?.close());
      this.gate?.addEventListener('click', (event) => {
        if (event.target === this.gate) this.gate.close();
      });

      this.checkoutClickHandler = (event) => this.handleCheckoutClick(event);
      document.addEventListener('click', this.checkoutClickHandler, true);
      this.swatches.forEach((swatch) => {
        swatch.addEventListener('click', () => {
          const variant = this.variants.find(
            (candidate) => String(candidate.id) === String(swatch.dataset.ccPairSwatch)
          );
          if (variant?.available) this.renderOffer(variant);
        });
      });

      this.refresh();
    }

    disconnectedCallback() {
      if (this.checkoutClickHandler) {
        document.removeEventListener('click', this.checkoutClickHandler, true);
      }
    }

    async getCart() {
      const response = await fetch(route('cart.js'), {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Unable to load your cart.');
      return response.json();
    }

    getColor(item) {
      const colorOption = (item.options_with_values || []).find((option) =>
        normalize(option.name).includes('color')
      );
      return colorOption?.value || item.variant_options?.[0] || '';
    }

    getMatchingVariant(color) {
      const normalizedColor = normalize(color);
      return (
        this.variants.find(
          (variant) => variant.available && normalize(variant.color) === normalizedColor
        ) ||
        this.variants.find((variant) => variant.available)
      );
    }

    renderOffer(variant) {
      if (!this.cart || !this.chairItem || !variant) return;

      this.currentVariant = variant;
      const currency =
        this.cart.currency || window.Shopify?.currency?.active || 'USD';
      const existingChairPrice = Number(
        this.chairItem.original_price || this.chairItem.price || 0
      );
      const regularPairTotal = existingChairPrice + Number(variant.price || 0);
      const discountedPairTotal = Math.round(regularPairTotal * 0.95);
      const currentSubtotal = Number(
        this.cart.items_subtotal_price || this.cart.original_total_price || 0
      );
      const currentChairLineTotal = Number(
        this.chairItem.final_line_price ??
        this.chairItem.original_line_price ??
        existingChairPrice
      );
      const projectedSubtotal =
        currentSubtotal - currentChairLineTotal + discountedPairTotal;
      const unlocksFreeShipping =
        currentSubtotal < this.threshold && projectedSubtotal >= this.threshold;
      const alreadyQualifies = currentSubtotal >= this.threshold;

      const image = this.querySelector('[data-cc-pair-image]');
      if (image && variant.image) image.src = variant.image;
      if (image && variant.alt) image.alt = variant.alt;

      const title = this.querySelector('[data-cc-pair-title]');
      const badge = this.querySelector('[data-cc-pair-badge]');
      const note = this.querySelector('[data-cc-pair-note]');
      if (unlocksFreeShipping) {
        if (title) title.textContent = 'Make it a Pair — Save 5% + Unlock Free Shipping';
        if (badge) badge.textContent = 'Save 5% + Free Shipping';
        if (note) {
          note.textContent =
            'Chair discount applies automatically. Your pair clears the $100 free U.S. shipping threshold.';
        }
      } else if (alreadyQualifies) {
        if (title) title.textContent = 'Make it a Pair — Save 5%';
        if (badge) badge.textContent = 'Save 5%';
        if (note) {
          note.textContent =
            'Chair discount applies automatically. Your cart already qualifies for free U.S. shipping.';
        }
      } else {
        if (title) title.textContent = 'Make it a Pair — Save 5%';
        if (badge) badge.textContent = 'Save 5%';
        if (note) {
          note.textContent =
            'Chair discount applies automatically. Free U.S. shipping starts at $100.';
        }
      }

      const variantText = this.querySelector('[data-cc-pair-variant]');
      if (variantText) variantText.textContent = String(variant.color || variant.title) + ' selected';

      const originalPrice = this.querySelector('[data-cc-pair-original]');
      if (originalPrice) originalPrice.textContent = formatMoney(regularPairTotal, currency);

      const pairTotal = this.querySelector('[data-cc-pair-total]');
      if (pairTotal) pairTotal.textContent = formatMoney(discountedPairTotal, currency) + ' pair';

      this.swatches.forEach((swatch) => {
        const selected = String(swatch.dataset.ccPairSwatch) === String(variant.id);
        swatch.classList.toggle('is-selected', selected);
        swatch.setAttribute('aria-pressed', String(selected));
      });

      if (this.button) {
        this.button.textContent =
          'Add second chair — ' + formatMoney(discountedPairTotal, currency) + ' pair';
      }
      const productSavings = regularPairTotal - discountedPairTotal;
      const shippingSavings = unlocksFreeShipping ? this.shippingCost : 0;
      this.renderCheckoutGate({
        variant,
        currency,
        discountedPairTotal,
        projectedSubtotal,
        productSavings,
        shippingSavings,
        unlocksFreeShipping,
        alreadyQualifies
      });

      this.hidden = false;
    }

    renderCheckoutGate({
      variant,
      currency,
      discountedPairTotal,
      projectedSubtotal,
      productSavings,
      shippingSavings,
      unlocksFreeShipping,
      alreadyQualifies
    }) {
      if (!this.gate) return;

      const totalSavings = productSavings + shippingSavings;
      const title = this.gate.querySelector('[data-cc-pair-gate-title]');
      const savings = this.gate.querySelector('[data-cc-pair-gate-savings]');
      const breakdown = this.gate.querySelector('[data-cc-pair-gate-breakdown]');
      const total = this.gate.querySelector('[data-cc-pair-gate-total]');
      const selection = this.gate.querySelector('[data-cc-pair-gate-selection]');

      if (title) {
        title.textContent = unlocksFreeShipping
          ? 'Add a Second Chair — Save 5% + Free Shipping'
          : 'Add a Second Chair — Save 5%';
      }
      if (savings) savings.textContent = 'Save ' + formatMoney(totalSavings, currency) + ' total';

      if (breakdown) {
        if (unlocksFreeShipping) {
          breakdown.textContent =
            formatMoney(productSavings, currency) + ' chair discount + ' +
            formatMoney(this.shippingCost, currency) + ' shipping saved';
        } else if (alreadyQualifies) {
          breakdown.textContent =
            formatMoney(productSavings, currency) + ' chair discount · Your cart already ships free';
        } else {
          breakdown.textContent = formatMoney(productSavings, currency) + ' chair discount';
        }
      }

      if (total) {
        const projectedShipsFree =
          unlocksFreeShipping || alreadyQualifies || projectedSubtotal >= this.threshold;
        total.textContent =
          formatMoney(projectedSubtotal, currency) +
          (projectedShipsFree ? ' shipped' : ' before shipping');
      }
      if (selection) {
        selection.textContent =
          String(variant.color || variant.title) + ' selected · Before tax.';
      }
      if (this.gateAdd) {
        this.gateAdd.disabled = false;
        this.gateAdd.textContent = 'Add Second Chair + Checkout';
      }
    }

    async handleCheckoutClick(event) {
      const trigger = event.target.closest(
        '[name="checkout"], #CartDrawer-Checkout, #checkout'
      );
      if (!trigger || this.hidden) return;

      if (this.checkoutBypass) {
        this.checkoutBypass = false;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      this.checkoutTrigger = trigger;

      try {
        const cart = await this.getCart();
        const chairItems = (cart.items || []).filter(
          (item) => item.handle === CHAIR_HANDLE
        );
        const chairQuantity = chairItems.reduce(
          (total, item) => total + Number(item.quantity || 0),
          0
        );

        if (chairQuantity !== 1) {
          this.resumeCheckout();
          return;
        }

        this.cart = cart;
        this.chairItem = chairItems[0];
        const variant =
          this.currentVariant ||
          this.getMatchingVariant(this.getColor(this.chairItem));

        if (!variant) {
          this.resumeCheckout();
          return;
        }

        this.renderOffer(variant);
        if (this.gate && !this.gate.open) this.gate.showModal();
      } catch (error) {
        this.resumeCheckout();
      }
    }

    resumeCheckout() {
      const trigger = this.checkoutTrigger;
      this.checkoutTrigger = null;

      if (trigger) {
        this.checkoutBypass = true;
        trigger.click();
        return;
      }

      window.location.href = route('checkout');
    }

    async refresh() {
      if (this.refreshing || !this.variants.length) return;
      this.refreshing = true;

      try {
        const cart = await this.getCart();
        const chairItems = (cart.items || []).filter(
          (item) => item.handle === CHAIR_HANDLE
        );
        const chairQuantity = chairItems.reduce(
          (total, item) => total + Number(item.quantity || 0),
          0
        );

        if (chairQuantity !== 1) {
          this.hidden = true;
          return;
        }

        this.cart = cart;
        this.chairItem = chairItems[0];
        const variant = this.getMatchingVariant(this.getColor(this.chairItem));
        if (!variant) {
          this.hidden = true;
          return;
        }

        this.renderOffer(variant);
      } catch (error) {
        this.hidden = true;
      } finally {
        this.refreshing = false;
      }
    }

    renderCart(cart) {
      if (document.querySelector('cart-items')) {
        window.location.reload();
        return;
      }

      const drawer = document.querySelector('cart-drawer');
      if (drawer?.renderContents && cart.sections) {
        drawer.renderContents(cart);
        return;
      }

      window.location.href = route('cart');
    }

    async addChair(continueToCheckout = false) {
      const actionButton = continueToCheckout ? this.gateAdd : this.button;
      if (!this.currentVariant || actionButton?.disabled) return;
      actionButton.disabled = true;
      actionButton.textContent = 'Adding…';
      if (this.status) this.status.textContent = '';

      try {
        const addResponse = await fetch(route('cart/add.js'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [{ id: this.currentVariant.id, quantity: 1 }]
          })
        });
        const added = await addResponse.json();
        if (!addResponse.ok) {
          throw new Error(added.description || 'Unable to add the second chair.');
        }

        const updateResponse = await fetch(route('cart/update.js'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname || '/cart'
          })
        });
        const cart = await updateResponse.json();
        if (!updateResponse.ok) {
          throw new Error(cart.description || 'Unable to refresh your cart.');
        }

        if (continueToCheckout) {
          this.gate?.close();
          window.location.href = route('checkout');
          return;
        }

        this.renderCart(cart);
      } catch (error) {
        if (this.status) {
          this.status.textContent =
            error?.message || 'We could not add the second chair. Please try again.';
        }
        actionButton.disabled = false;
        if (continueToCheckout && this.gateStatus) {
          this.gateStatus.textContent =
            error?.message || 'We could not add the second chair. Please try again.';
        }
        this.renderOffer(this.currentVariant);
      }
    }
  }

  if (!customElements.get('cc-cart-pair-upsell')) {
    customElements.define('cc-cart-pair-upsell', CCCartPairUpsell);
  }
})();
